import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Bus, MapPin, Smartphone, CheckCircle } from 'lucide-react';

const steps = [
  {
    icon: <Smartphone size={36} className="text-primary-600" />,
    title: 'Download the App',
    description: 'Install our mobile app and create your account in seconds.',
  },
  {
    icon: <MapPin size={36} className="text-accent-600" />,
    title: 'Set Your Destination',
    description: 'Enter your current location and where you want to go.',
  },
  {
    icon: <Bus size={36} className="text-primary-600" />,
    title: 'AI Plans Your Route',
    description: 'Our AI analyzes real-time data to find the optimal route.',
  },
  {
    icon: <CheckCircle size={36} className="text-accent-600" />,
    title: 'Enjoy Your Journey',
    description: 'Receive updates and enjoy a stress-free commute.',
  },
];

const seededRandom = (seed: number) => {
  const x = Math.sin(seed + 1) * 10000;
  return x - Math.floor(x);
};

const CloudsSection = React.memo(() => {
  const clouds = useMemo(() =>
    Array.from({ length: 5 }, (_, i) => ({
      width: seededRandom(i * 7) * 100 + 50,
      height: seededRandom(i * 11) * 50 + 30,
      top: seededRandom(i * 13) * 100,
      left: seededRandom(i * 17) * 100,
      duration: 30 + seededRandom(i * 19) * 30,
      delay: seededRandom(i * 23) * 10,
    })), []
  );

  return (
    <div className="absolute inset-0 overflow-hidden">
      {clouds.map((cloud, i) => (
        <motion.div
          key={i}
          className="absolute bg-white bg-opacity-20 rounded-full"
          style={{ width: `${cloud.width}px`, height: `${cloud.height}px`, top: `${cloud.top}px`, left: `${cloud.left}%` }}
          animate={{ x: ["0%", "100%"], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: cloud.duration, repeat: Infinity, repeatType: "loop", delay: cloud.delay }}
        />
      ))}
    </div>
  );
});
CloudsSection.displayName = 'CloudsSection';

const ScenerySection = React.memo(() => {
  const sceneryItems = useMemo(() =>
    Array.from({ length: 15 }, (_, i) => {
      const isBuilding = seededRandom(i * 31) > 0.4;
      const height = isBuilding ? seededRandom(i * 37) * 40 + 30 : seededRandom(i * 41) * 30 + 20;
      const width = isBuilding ? seededRandom(i * 43) * 30 + 20 : seededRandom(i * 47) * 15 + 10;
      const delay = seededRandom(i * 53) * 15;
      const duration = 40 + seededRandom(i * 59) * 40;
      const left = seededRandom(i * 61) * 120;
      const zIndex = Math.floor(seededRandom(i * 67) * 5);
      const floors = Math.floor(height / 10);
      const windowsPerFloor = Math.floor(width / 8);
      const windowDurations = Array.from({ length: floors }, (_, f) =>
        Array.from({ length: windowsPerFloor }, (_, w) => ({
          dur: 10 + seededRandom(i * 100 + f * 10 + w) * 20,
          del: seededRandom(i * 200 + f * 10 + w) * 5,
        }))
      );
      const foliageDuration = 5 + seededRandom(i * 71) * 10;
      const treeDetails = Array.from({ length: 5 }, (_, d) => ({
        w: seededRandom(i * 300 + d * 7) * 15 + 5,
        h: seededRandom(i * 400 + d * 11) * 15 + 5,
        top: seededRandom(i * 500 + d * 13) * 60,
        left: seededRandom(i * 600 + d * 17) * 80 + 10,
      }));
      return { isBuilding, height, width, delay, duration, left, zIndex, floors, windowsPerFloor, windowDurations, foliageDuration, treeDetails };
    }), []
  );

  return (
    <div className="absolute bottom-32 w-full h-32 overflow-hidden">
      {sceneryItems.map((item, i) => (
        <motion.div
          key={i}
          className="absolute bottom-0"
          style={{ left: `${item.left}%`, zIndex: item.zIndex }}
          animate={{ x: ["100%", `-${item.width}px`] }}
          transition={{ duration: item.duration, repeat: Infinity, ease: "linear", delay: item.delay }}
        >
          {item.isBuilding ? (
            <div className="relative bg-gradient-to-b from-gray-600 to-gray-700" style={{ width: `${item.width}px`, height: `${item.height}px` }}>
              <div className="absolute inset-0 flex flex-col">
                {Array.from({ length: item.floors }, (_, floor) => (
                  <div key={floor} className="flex-1 flex border-t border-gray-500">
                    {Array.from({ length: item.windowsPerFloor }, (_, w) => (
                      <motion.div
                        key={w}
                        className="flex-1 m-0.5 bg-gray-400 rounded-sm"
                        animate={{ backgroundColor: ['rgb(156 163 175)', 'rgb(251 191 36)', 'rgb(156 163 175)'] }}
                        transition={{ duration: item.windowDurations[floor]?.[w]?.dur ?? 15, repeat: Infinity, delay: item.windowDurations[floor]?.[w]?.del ?? 0 }}
                      />
                    ))}
                  </div>
                ))}
              </div>
              <div className="absolute -top-1 left-0 right-0 h-2 bg-gray-800 rounded-t-sm"></div>
            </div>
          ) : (
            <div className="relative" style={{ width: `${item.width}px`, height: `${item.height}px` }}>
              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-2 bg-gradient-to-b from-yellow-800 to-yellow-700" style={{ height: `${item.height * 0.4}px` }}></div>
              <motion.div
                className="absolute bottom-0 left-0 right-0 rounded-full bg-green-600"
                style={{ height: `${item.height * 0.7}px`, bottom: `${item.height * 0.3}px` }}
                animate={{ scale: [1, 1.05, 1], backgroundColor: ['rgb(22 163 74)', 'rgb(21 128 61)', 'rgb(22 163 74)'] }}
                transition={{ duration: item.foliageDuration, repeat: Infinity }}
              >
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-green-500/30 to-transparent"></div>
                {item.treeDetails.map((d, di) => (
                  <div key={di} className="absolute bg-green-700 rounded-full" style={{ width: `${d.w}px`, height: `${d.h}px`, top: `${d.top}%`, left: `${d.left}%` }}></div>
                ))}
              </motion.div>
            </div>
          )}
        </motion.div>
      ))}
    </div>
  );
});
ScenerySection.displayName = 'ScenerySection';

const HowItWorks: React.FC = React.memo(() => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-24 bg-white dark:!bg-ink-850 overflow-hidden">
      <div className="container mx-auto">
        <motion.div
          ref={ref}
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-black tracking-tight mb-4" style={{ fontSize: "clamp(28px, 4vw, 48px)", color: "#1D3047" }}>
            <span style={{ color: "#2D5F9E" }}>How Safer Savvy </span>
            <span style={{ color: "#E3B02E" }}>Works</span>
          </h2>
          <p className="max-w-3xl mx-auto text-gray-600 text-sm sm:text-base px-2 sm:px-0">
            Our intelligent platform simplifies your transportation experience with just a few simple steps.
            Let our AI handle the complexities while you enjoy the journey.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 relative">
          <div
            className="hidden lg:block absolute z-0 bg-gray-300"
            style={{ top: "34px", left: "calc(12.5%)", right: "calc(12.5%)", height: "2px" }}
          />
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="relative z-10 flex flex-col items-center text-center"
            >
              <div className="bg-white p-4 rounded-full shadow-lg mb-5 relative">
                {step.icon}
                <div className={`absolute -right-2 -top-2 w-6 h-6 rounded-full text-white flex items-center justify-center font-bold text-xs ${(index + 1) % 2 === 0 ? "bg-yellow-500" : "bg-primary-600"}`}>
                  {index + 1}
                </div>
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-primary-600 mb-2">{step.title}</h3>
              <p className="text-gray-600 text-sm">{step.description}</p>
            </motion.div>
          ))}
        </div>

        <div className="relative mt-20 h-64 w-full overflow-hidden">
          <CloudsSection />
          <div className="absolute bottom-0 w-full h-32 bg-gray-800 overflow-hidden">
            {Array.from({ length: 20 }, (_, i) => (
              <motion.div
                key={i}
                className="absolute h-1 bg-yellow-400 w-24"
                style={{ bottom: "40%", left: `${(i * 15) % 100}%` }}
                animate={{ x: ["0%", "-100%"] }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear", delay: i * 0.05 }}
              />
            ))}
          </div>
          <motion.div
            className="absolute bottom-32 left-0 flex items-end z-10"
            initial={{ x: "-20%" }}
            animate={{ x: ["-20%", "450%"], y: [35, 35, 35, 35, 35] }}
            transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
          >
            <div className="relative bg-gradient-to-b from-blue-700 to-blue-500 rounded-lg shadow-xl w-64 h-32 overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-10 bg-gradient-to-b from-blue-200 to-blue-500 opacity-30 rounded-t-lg"></div>
              <div className="absolute top-2 left-2 right-8 grid grid-cols-4 gap-1">
                {[0, 1, 2, 3].map((i) => (
                  <motion.div
                    key={i}
                    className="bg-sky-100 rounded-sm h-12 border-t-2 border-blue-300 relative overflow-hidden"
                    animate={{ background: ["linear-gradient(135deg, #e0f2fe 0%, #bae6fd 50%, #7dd3fc 100%)", "linear-gradient(135deg, #e0f2fe 0%, #bae6fd 30%, #7dd3fc 70%)"] }}
                    transition={{ duration: 7 + i * 2, repeat: Infinity, repeatType: "reverse" }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white opacity-20"></div>
                    {i < 3 && <div className="absolute right-[-0.75px] top-0 bottom-0 w-[1.5px] bg-blue-400"></div>}
                  </motion.div>
                ))}
              </div>
              <div className="absolute top-14 left-0 right-0 h-5 bg-gradient-to-r from-amber-500 to-amber-400 flex items-center justify-center">
                <motion.span
                  className="text-[9px] font-bold text-white uppercase tracking-wider"
                  animate={{ opacity: [0.8, 1, 0.8] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  Safer Savvy
                </motion.span>
              </div>
              <div className="absolute top-19 left-0 right-0 bottom-0 bg-blue-800">
                <div className="absolute left-3 top-1 w-8 h-12 bg-blue-700 rounded-sm border-l-2 border-blue-500">
                  <motion.div
                    className="absolute right-1 top-1/2 transform -translate-y-1/2 w-1.5 h-8 bg-blue-400 rounded-full"
                    animate={{ y: ["-50%", "-48%", "-50%"] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </div>
                <div className="absolute left-12 top-2 right-8 h-10 border-b-2 border-blue-700">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="absolute bottom-0 left-0 right-0 h-[1px] bg-blue-600" style={{ bottom: `${i * 4 + 4}px` }} />
                  ))}
                </div>
              </div>
              <div className="absolute bottom-[-12px] left-6 right-6 flex justify-between">
                {[0, 1].map((w) => (
                  <motion.div
                    key={w}
                    className="relative w-10 h-10 bg-gray-900 rounded-full border-4 border-gray-700"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
                  >
                    <div className="absolute inset-1 rounded-full bg-gray-700 border border-gray-600 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-gray-500"></div>
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="absolute w-1 h-3 bg-gray-500 rounded-full" style={{ transform: `rotate(${i * 72}deg) translateY(-6px)` }} />
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
              <div className="absolute right-0 top-6 bottom-6 w-8 bg-blue-900 rounded-r-lg flex flex-col items-center justify-around">
                {[...Array(5)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="w-full h-[1px] bg-blue-700"
                    animate={{ width: ["100%", "90%", "100%"] }}
                    transition={{ duration: 2, delay: i * 0.2, repeat: Infinity }}
                  />
                ))}
              </div>
              <div className="absolute right-2 bottom-8 flex space-x-1.5">
                {[0, 1].map((l) => (
                  <motion.div
                    key={l}
                    className="w-2.5 h-5 bg-amber-200 rounded-full"
                    animate={{ opacity: [0.8, 1, 0.8], boxShadow: "0 0 5px 2px rgba(251, 191, 36, 0.5)" }}
                    transition={{ duration: 1.5, delay: l * 0.5, repeat: Infinity }}
                  />
                ))}
              </div>
              <div className="absolute left-2 top-4 w-3 h-4 bg-gray-700 rounded-t-sm">
                <motion.div
                  className="absolute -top-2 left-0 w-3 h-2 bg-gray-300 rounded-full blur-sm"
                  animate={{ y: [-2, -6, -10], opacity: [1, 0.5, 0], scale: [1, 1.5, 2] }}
                  transition={{ duration: 3, repeat: Infinity, repeatDelay: 1 }}
                />
              </div>
            </div>
          </motion.div>
          <ScenerySection />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-16 text-center"
        >
          <button className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-full font-medium text-sm sm:text-base transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1">
            Try Safer Savvy Today
          </button>
        </motion.div>
      </div>
    </section>
  );
});

HowItWorks.displayName = 'HowItWorks';
export default HowItWorks;
